import type { CurrentRefinementsConnectorParamsItem } from 'instantsearch.js/es/connectors/current-refinements/connectCurrentRefinements'

/**
 * Represents a simplified filter with a label and value.
 *
 * @typedef {Object} CurrentFilter
 * @property {string} filterLabel - The attribute or label of the filter.
 * @property {string} filterValue - The value of the filter.
 */
export type CurrentFilter = {
  filterLabel: string
  filterValue: string
}

/**
 * Simplifies Algolia filters by extracting filter attributes and values.
 *
 * @param {CurrentRefinementsConnectorParamsItem[]} filters - The Algolia refinement filters.
 * @returns {CurrentFilter[]} An array of simplified filters with labels and values.
 * @description Transforms complex Algolia refinement filters into a simplified format.
 * @example
 * // Returns [{ filterLabel: 'color', filterValue: 'red,blue' }]
 * AlgoliaSimplifyFilters([{ attribute: 'color', refinements: [{ value: 'red' }, { value: 'blue' }] }])
 */
export const AlgoliaSimplifyFilters = (
  filters: CurrentRefinementsConnectorParamsItem[]
) => {
  return filters.map(currentFilter => {
    const value = currentFilter.refinements.map(refinement => refinement.value)
    return {
      filterLabel: currentFilter.attribute,
      filterValue: value.toString(),
    }
  })
}

/**
 * Compares two arrays of filters to determine added and removed filters.
 *
 * @param {CurrentFilter[]} oldArray - The original array of filters.
 * @param {CurrentFilter[]} newArray - The new array of filters.
 * @returns {Object} An object containing added filters, removed filters, and equality status.
 * @property {CurrentFilter[]} filterToAdd - Filters present in the new array but not in the old array.
 * @property {CurrentFilter[]} filterToRemove - Filters present in the old array but not in the new array.
 * @property {boolean} areEqual - Indicates whether the filter arrays are identical.
 * @description Compares two filter arrays and identifies changes between them.
 * @example
 * // Returns { filterToAdd: [{ filterLabel: 'color', filterValue: 'red' }], filterToRemove: [], areEqual: false }
 * compareArraysFilters([], [{ filterLabel: 'color', filterValue: 'red' }])
 */
export const compareArraysFilters = (
  oldArray: CurrentFilter[],
  newArray: CurrentFilter[]
): {
  filterToAdd: CurrentFilter[]
  filterToRemove: CurrentFilter[]
  areEqual: boolean
} => {
  const filterToAdd: CurrentFilter[] = []
  const filterToRemove: CurrentFilter[] = []

  const oldSet = new Set(
    oldArray.map(item => `${item.filterLabel}:${item.filterValue}`)
  )
  const newSet = new Set(
    newArray.map(item => `${item.filterLabel}:${item.filterValue}`)
  )

  for (const item of newArray) {
    const key = `${item.filterLabel}:${item.filterValue}`
    if (!oldSet.has(key)) {
      filterToAdd.push(item)
    }
  }

  for (const item of oldArray) {
    const key = `${item.filterLabel}:${item.filterValue}`
    if (!newSet.has(key)) {
      filterToRemove.push(item)
    }
  }

  const areEqual = filterToAdd.length === 0 && filterToRemove.length === 0

  return { filterToAdd, filterToRemove, areEqual }
}
